import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    SimpleChanges,
} from "@angular/core";
import OverlayScrollbars from "overlayscrollbars";
import { Group } from "src/AngularJS/Models/group.model";
import { WorkAidsService } from "src/app/work-aids/work-aids.service";
import { AngularAssignToolService } from "../../assign-tool/angular-assign-tool.service";
import { CVLocalizationService } from "../../services/cv-localization.service";
import { GridApi } from "ag-grid-community";
import { AnnouncementsService } from "src/app/announcements/announcements.service";
import { ProfileApiModel } from "src/shared/models/dashboard.model";
import { BroadcastProfileApiModel } from "./broadcast-assign.models";

@Component({
    selector: "cv-broadcast-assign",
    templateUrl: "./broadcast-assign.component.html",
    styleUrls: ["./broadcast-assign.component.scss"],
})
export class BroadcastAssignComponent implements OnInit {
    /** The item currently being edited inside of the right-box. */
    @Input() activeSelection: Group = null;
    @Output() activeSelectionChange = new EventEmitter<Group>();
    @Input() onRemove;
    @Input() dataList;
    @Input() popupButtonEnabled: boolean = false;
    /** Controls whether the other two boxes on the assign tool are disabled or not. */
    @Input() disableContainers = false;
    @Output() disableContainersChange = new EventEmitter<boolean>(true);
    /** Controls whether the apply button on the assign tool is enabled or not. */
    @Input() enableContainerButtons = false;
    @Output() enableContainerButtonsChange = new EventEmitter<boolean>(true);
    @Input() saveBroadcast: EventEmitter<any[]>;

    gridApi: GridApi;
    hasInitialized: boolean = false;
    checkAll: boolean = false;
    selectedGroup: any; // The group recently selected by the assign tool/currently edited by this directive (assigning access to roles)
    groupAssignments: { [key: string]: any } = {}; // The groups that are assigned to the dashboard share (these will be saved to the database when the apply button is clicked)
    groupAssignmentsRolesFiltered = [];

    filterValue: string = "";
    roleAssign = {
        // The selected group level's roles (used as a template when adding the selected group to the group assignments)
        groupType: null,
        groupLevel: -1,
        show: true, // If the role assignment is in process
        rolesLoading: false, // The roles are currently loading from the endpoint
        rolesLoadSuccess: false, // The roles were loaded successfully from the endpoint
        roles: [], // The list of roles for the group
    };

    public scrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            autoHideDelay: 100,
            theme: "os-theme-dark",
            visibility: "auto",
        },
    };

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private workAidsService: WorkAidsService,
        private announcementsService: AnnouncementsService,
        private angularAssignToolService: AngularAssignToolService
    ) {}

    async ngOnInit() {
        await this.cvLocalizationService.GetSection("Assign_Tool");
        this.getRoles();

        this.popupButtonEnabled = false;

        if (this.saveBroadcast) {
            this.saveBroadcast.subscribe(() => {
                this.saveAllGroupAssignments();
            });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes?.activeSelection?.currentValue) {
            this.onSelectionChange();
        }
        if (changes?.popupButtonEnabled?.currentValue) {
            // when the Assign modal opens, set the groupAssignments
            if (this.dataList.length <= 0) this.resetAssignments();

            if (this.dataList?.length >= 0) {
                const selectedKeys = [];
                this.dataList.forEach((recipient) => {
                    selectedKeys.push(
                        recipient.GroupLevel + "-" + recipient.GroupID
                    );
                });

                // Delete any groupAssignments not in the assignList
                // due to them being deleted from the Work Aids New page
                Object.keys(this.groupAssignments).forEach((key) => {
                    if (!selectedKeys.includes(key)) {
                        delete this.groupAssignments[key];
                    }
                });

                this.filterGroupAssignmentsRoles();
            }
        }
    }

    /**
     * Sets the enabled state of the "popup button" on the assign tool. (In this case, this is the assign button.)
     */
    updatePopupButtonEnabled() {
        // Enable the button if groups have been assigned
        // Groups
        for (let prop in this.groupAssignments) {
            if (this.groupAssignments.hasOwnProperty(prop)) {
                this.popupButtonEnabled = true;
                return;
            }
        }

        // Disable the button if no groups have been assigned
        this.popupButtonEnabled = false;
    }

    /**
     * Deletes a group from the assigned groups list if it exists and adds it back to the assign tool's list.
     *
     * @param {GroupDTO} group The group to be deleted
     */
    deleteGroup(assignment) {
        // Delete the group
        if (
            this.groupAssignments[
                assignment.group.GroupLevel + "-" + assignment.group.GroupID
            ]
        )
            delete this.groupAssignments[
                assignment.group.GroupLevel + "-" + assignment.group.GroupID
            ];
        
        this.filterGroupAssignmentsRoles();
        // Remove it from the recipient list
        for (let i = 0; i < this.workAidsService.save.RecipientAssignments.length; i++) {
            if (
                this.roleObjectsAreEqual(
                    this.workAidsService.save.RecipientAssignments[i],
                    assignment
                )
            )
                this.workAidsService.save.RecipientAssignments.splice(i, 1);
        }
        for (let i = 0; i < this.announcementsService.save.RecipientAssignments.length; i++) {
            if (
                this.roleObjectsAreEqual(
                    this.announcementsService.save.RecipientAssignments[i],
                    assignment
                )
            )
                this.workAidsService.save.RecipientAssignments.splice(i, 1);
        }

        // Add the group back into the assign tool
        const groupDTO = assignment.group;
        let i = this.dataList.length;
        while (i--) {
            if (
                this.dataList[i].GroupID &&
                this.dataList[i].GroupID === groupDTO.GroupID
            )
                this.dataList.splice(i, 1);
        }
        this.onRemove({ dto: groupDTO });
        this.updatePopupButtonEnabled();
    }

    /**
     * Event handler for clicking the edit button on an assigned group. Allows the user to continue
     * selecting access for roles.
     *
     * @param {GroupDTO} object The group to be re-selected for editing
     */
    editAssignment(object) {
        this.selectedGroup = object;

        this.disableContainers = true;

        if (this.gridApi != null) {
            const data = angular.copy(
                this.selectedGroup.roleAssign.tableModel.rowData
            );

            this.gridApi.setGridOption('rowData', data);
            this.gridApi.onFilterChanged();
        }

        this.checkAll = !object?.roleAssign?.tableModel?.rowData.some(
            (o) => !o.send
        );
    }

    /**
     * Event handler for the save button used for assigning roles.
     * Moves the selected group into this.groupAssignments and ends the role access selection.
     */
    roleAssignmentSave() {
        if (!this.assignButtonEnabled("role")) return;

        const selectedGroupKey =
            this.selectedGroup.group.GroupLevel +
            "-" +
            this.selectedGroup.group.GroupID;
        this.groupAssignments[selectedGroupKey] = this.selectedGroup;
        this.disableContainers = false;
        this.updatePopupButtonEnabled();

        const roles =
            this.groupAssignments[selectedGroupKey].roleAssign.tableModel
                .rowData;
        for (let i = 0; i < roles.length; i++) {
            if (roles[i].send) {
                roles[i].pristineSend = roles[i].send;
            }
        }
        this.filterGroupAssignmentsRoles();
        this.clearFilter();
        this.selectedGroup = null;
        this.disableContainersChange.emit(false);
        this.enableContainerButtonsChange.emit(true);
        this.checkAll = false;
    }

    /**
     * Event handler for the cancel button used for assigning roles.
     * Deletes the selected group and adds it back into the assign tool's list if the selected group was just added
     * and had not yet been saved to the assigned groups list.
     */
    roleAssignmentCancel() {
        const groupKey =
            this.selectedGroup.group.GroupLevel +
            "-" +
            this.selectedGroup.group.GroupID;
        if (this.groupAssignments[groupKey]) {
            // Group was pre-populated from the list of existing dashboard assignments
            const profiles =
                this.groupAssignments[groupKey].roleAssign.tableModel.rowData;
            profiles.forEach(function (profile) {
                profile.send = profile.pristineSend;
            });
        } else {
            this.deleteGroup(this.selectedGroup); // Add group back into list of groups
        }
        this.filterGroupAssignmentsRoles();
        this.clearFilter();
        this.selectedGroup = null;
        this.disableContainersChange.emit(false);
        this.enableContainerButtonsChange.emit(true);
        this.checkAll = false;
    }

    selectAll() {
        this.selectedGroup.roleAssign.tableModel.rowData.forEach((listItem) => {
            listItem.send = !this.checkAll;
        });

        if (this.gridApi != null) {
            this.gridApi.refreshCells();
            this.gridApi.onFilterChanged();
        }
    }

    /**
     * Resets the data objects on the controller
     */
    reset() {
        if (this.selectedGroup != null) {
            this.roleAssignmentCancel();
        }
        this.selectedGroup = null;
        this.disableContainers = false;

        // Reset the role assign template
        this.roleAssign.groupType = null;
        this.roleAssign.groupLevel = -1;
        this.roleAssign.show = true;
        this.roleAssign.rolesLoading = false;
        this.clearFilter();
    }

    resetAssignments() {
        this.groupAssignments = {};
    }

    onSelectionChange() {
        this.checkAll = false;
        this.disableContainersChange.emit(true);
        this.enableContainerButtonsChange.emit(false);
        this.selectGroup();
    }

    saveAllGroupAssignments() {
        const sendObjs = [];
        for (const groupID in this.groupAssignments) {
            // Groups
            const sendObj = {
                group: this.groupAssignments[groupID].group,
                roles: [],
            };
            const roles =
                this.groupAssignments[groupID].roleAssign.tableModel.rowData;
            for (let j = 0; j < roles.length; j++) {
                // Roles
                if (roles[j].send) {
                    sendObj.roles.push(roles[j]);
                }
            }
            sendObjs.push(sendObj);
        }
        
        this.workAidsService.save.RecipientAssignments = sendObjs;
        this.announcementsService.save.RecipientAssignments = sendObjs;
    }

    /**
     * Sets this.selectedGroup and sets up Ag-Grid for a Group. This is the first step in allowing the user to select
     * read access for roles within the group.
     */
    selectGroup() {
        const groupRoles = angular.copy(this.roleAssign.roles);

        this.selectedGroup = this.createGroupModel(
            this.activeSelection,
            groupRoles
        );
        this.disableContainers = true;
    }

    /**
     * Creates a group model used by this directive from a given groupDTO
     *
     * @returns {object} The group model used for this.selectedGroup and this.groupAssignments
     */
    createGroupModel(groupDTO, groupRoles) {
        return {
            group: groupDTO == null ? null : groupDTO,
            roleAssign: {
                filterValue: "",
                groupLevel:
                    this.activeSelection == null
                        ? null
                        : this.activeSelection.GroupLevel,
                groupType:
                    this.activeSelection == null
                        ? null
                        : this.activeSelection.BaseGroupType,
                tableModel: {
                    columnDefs: [
                        {
                            field: "ProfileName",
                            headerName:
                                this.cvLocalizationService.getCachedText(
                                    "Assign_Tool",
                                    "Roles",
                                    "Roles"
                                ),
                            suppressMenu: true,
                            width: 200,
                        },
                        {
                            field: "send",
                            headerName:
                                this.cvLocalizationService.getCachedText(
                                    "Assign_Tool",
                                    "Assign",
                                    "Assign"
                                ),
                            width: 120,
                            suppressMenu: true,
                            headerClass: "ag-header-center",
                            cellRenderer: function (params) {
                                return `<div class="form-check d-flex justify-content-center"><input class="form-check-input" type="checkbox"${
                                    params.data.send ? ' checked="checked"' : ""
                                }/></div>`;
                            },
                        },
                    ],
                    rowData: groupRoles == null ? [] : groupRoles,
                    getRowNodeId: (rowData) => rowData.ProfileID,

                    rowSelection: "multiple",
                    rowMultiSelectWithClick: false,

                    suppressContextMenu: true,
                    suppressRowClickSelection: true,
                    suppressCellSelection: true,

                    defaultColDef: {
                        sortable: false,
                        filter: true,
                    },
                    components: {},
                    onCellClicked: (params) => {
                        const colId = params.column.getId();

                        switch (colId) {
                            case "send":
                                params.node.data.send = !params.node.data.send;
                                this.updateRowData(params);
                                break;
                        }
                    },
                    onGridReady: (params) => {
                        this.gridApi = params.api;
                        params.api.sizeColumnsToFit();
                    },
                    onGridSizeChanged: (e) => {
                        e.api.sizeColumnsToFit();
                    },
                    animateRows: true,
                    rowHeight: 35,
                    isExternalFilterPresent: () => true,
                    doesExternalFilterPass: (node) =>
                        node.data.ProfileName?.toLowerCase().includes(
                            this.filterValue.toLowerCase()
                        ),
                    popupParent: document.querySelector("body"),
                    alwaysShowVerticalScroll: true,

                    enableRangeSelection: true,
                },
            },
        };
    }

    /**
     * Updates a row model used by this directive
     *
     * @param {params} params
     * @returns {object} The group model used for this.selectedGroup and this.groupAssignments
     */
    updateRowData(params) {
        if (this.gridApi != undefined) {
            const itemsToUpdate = [];
            this.gridApi.forEachNodeAfterFilterAndSort(function (rowNode, index) {
                if (index == params.rowIndex) {
                    const data = rowNode.data;
                    itemsToUpdate.push(data);
                    return;
                }
            });
            this.gridApi.applyTransaction({
                update: itemsToUpdate,
            });
        }
    }

    /**
     * Gets the roles and adds them to this.roleAssign.roles
     */
    async getRoles() {
        this.roleAssign.rolesLoading = true;

        const response: ProfileApiModel[] = await this.angularAssignToolService.loadRoles();

        if (response) {
            this.roleAssign.roles = [];
            for (let i = 0; i < response.length; i++) {
                let role = new BroadcastProfileApiModel(response[i]);
                role.send = false; // Set default read/write
                role.pristineSend = role.send;
                this.roleAssign.roles.push(role);
            }

            this.roleAssign.rolesLoading = false;
            this.roleAssign.rolesLoadSuccess = true;
        } else {
            this.roleAssign.rolesLoading = false;
            this.roleAssign.rolesLoadSuccess = false;
        }

        this.populateAssignments();
    }

    filterGroupAssignmentsRoles(){
        this.groupAssignmentsRolesFiltered = [];
        for(const key in this.groupAssignments){
            const roleAssignments = this.groupAssignments[key].roleAssign.tableModel.rowData.filter((role) => role.send)
            const obj = {
                key: key,
                group: this.groupAssignments[key].group,
                roleAssign: this.groupAssignments[key].roleAssign,
                roleAssignments: roleAssignments
            }
            this.groupAssignmentsRolesFiltered.push(obj);
        }
    }

    populateAssignments() {
        this.workAidsService.save.RecipientAssignments.forEach((recipient) => {
            const groupKey =
                recipient.group.GroupLevel + "-" + recipient.group.GroupID;
            this.groupAssignments[groupKey] = this.createGroupModel(
                recipient.group,
                recipient.roles
            );

            this.groupAssignments[
                groupKey
            ].roleAssign.tableModel.rowData.forEach((entry) => {
                entry.send = true;
                entry.pristineSend = true;
            });
        });

        this.announcementsService.save.RecipientAssignments.forEach((recipient) => {
            const groupKey =
                recipient.group.GroupLevel + "-" + recipient.group.GroupID;
            this.groupAssignments[groupKey] = this.createGroupModel(
                recipient.group,
                recipient.roles
            );

            this.groupAssignments[
                groupKey
            ].roleAssign.tableModel.rowData.forEach((entry) => {
                entry.send = true;
                entry.pristineSend = true;
            });
        });
    }

    applyFilter() {
        this.selectedGroup.roleAssign.filterValue = this.filterValue;
        if (this.gridApi != null) {
            // notify filter change
            this.gridApi.setGridOption('quickFilterText', this.filterValue.trim());
        }
    }

    clearFilter() {
        this.filterValue = "";
        if (this.gridApi != null) {
            // notify filter change
            this.gridApi.setGridOption('quickFilterText', this.filterValue);
        }
    }

    /**
     * Checks all assigned groups for any read assignemts.
     *
     * @returns {boolean} whether or not an assignment of the given type was found
     */
    checkAnyAssigned() {
        for (const groupName in this.groupAssignments) {
            if (this.checkAnyAssignedForGroup(groupName)) return true;
        }
        return false;
    }

    /**
     * Checks the given assigned group for any read assignments.
     *
     * @param {string} groupKey the key for the given group
     *
     * @returns {boolean} whether or not an assignment of the given type was found
     */
    checkAnyAssignedForGroup(groupKey, v = null, b = false) {
        const bool =
            this.groupAssignments[
                groupKey
            ].roleAssign.tableModel.rowData.filter((role) => {
                return role.send;
            }).length > 0;
        return bool;
    }

    /**
     * Determines if the assign "share" button should be enabled.
     */
    assignButtonEnabled(assignType) {
        if (assignType === "role") {
            const roles = this.selectedGroup.roleAssign.tableModel.rowData;
            for (let i = 0; i < roles.length; i++) {
                if (roles[i].send) return true;
            }
        }

        return false;
    }

    roleObjectsAreEqual(object1, object2) {
        const groupID = object1.group.GroupID == object2.group.GroupID;
        const groupLevel = object1.group.GroupLevel == object2.group.GroupLevel;
        const groupName = object1.group.GroupName == object2.group.GroupName;
        return groupID && groupLevel && groupName;
    }
}
